import React from 'react';
import styled from 'styled-components';

import {getRentalSettings, getVerificationCatalog, updateRentalSettings} from '../../api/rental';
import * as colors from '../../util/colors';
import Layout from './Layout';
import {checkTitle} from './verification-view';
import {
  ActionButton,
  Actions,
  Card,
  CardTitle,
  CheckboxRow,
  ErrorText,
  Field,
  FieldGrid,
  Grid,
  Input,
  State,
  SuccessText,
} from './styles';

const rublesFromKopecks = value => (
  value === null || value === undefined || value === '' ? '' : Number(value) / 100
);

const kopecksFromRubles = value => (
  value === null || value === undefined || value === '' ? null : Math.round(Number(value) * 100)
);

const toForm = settings => ({
  min_rental_days: settings.min_rental_days,
  max_rental_days: settings.max_rental_days,
  deposit_amount_rub: rublesFromKopecks(settings.deposit_amount),
  payment_hold_minutes: settings.payment_hold_minutes,
  payment_retry_minutes: settings.payment_retry_minutes,
  mileage_limit_per_day: settings.mileage_limit_per_day === null ? '' : settings.mileage_limit_per_day,
  mileage_overrun_price_rub: rublesFromKopecks(settings.mileage_overrun_price),
  liability_amount_rub: rublesFromKopecks(settings.liability_amount),
  free_cancellation_hours: settings.free_cancellation_hours,
  return_reminder_offsets: (settings.return_reminder_offsets || []).join(', '),
  handover_by_operator: settings.handover_by_operator !== false,
  no_show_release_minutes: settings.no_show_release_minutes,
  default_pickup_time: settings.default_pickup_time || '12:00',
  service_buffer_minutes: settings.service_buffer_minutes,
  overdue_hour_price_rub: rublesFromKopecks(settings.overdue_hour_price),
  late_cancellation_fee_percent: settings.late_cancellation_fee_percent,
  insurance_included: settings.insurance_included !== false,
  verification_auto_checks: settings.verification_auto_checks || [],
  min_driver_age: settings.min_driver_age === null || settings.min_driver_age === undefined
    ? '' : settings.min_driver_age,
  min_driving_experience_years: settings.min_driving_experience_years === null
    || settings.min_driving_experience_years === undefined
    ? '' : settings.min_driving_experience_years,
});

const nullableInt = value => (value === '' || value === null || value === undefined ? null : Number(value));

const toPayload = form => ({
  min_rental_days: Number(form.min_rental_days),
  max_rental_days: Number(form.max_rental_days),
  deposit_amount: kopecksFromRubles(form.deposit_amount_rub) || 0,
  payment_hold_minutes: Number(form.payment_hold_minutes),
  payment_retry_minutes: Number(form.payment_retry_minutes),
  mileage_limit_per_day: form.mileage_limit_per_day === '' ? null : Number(form.mileage_limit_per_day),
  mileage_overrun_price: kopecksFromRubles(form.mileage_overrun_price_rub),
  liability_amount: kopecksFromRubles(form.liability_amount_rub),
  free_cancellation_hours: Number(form.free_cancellation_hours),
  return_reminder_offsets: String(form.return_reminder_offsets)
    .split(',')
    .map(item => Number(item.trim()))
    .filter(item => Number.isInteger(item) && item > 0),
  handover_by_operator: Boolean(form.handover_by_operator),
  no_show_release_minutes: Number(form.no_show_release_minutes),
  default_pickup_time: form.default_pickup_time,
  service_buffer_minutes: Number(form.service_buffer_minutes) || 0,
  overdue_hour_price: kopecksFromRubles(form.overdue_hour_price_rub) || 0,
  late_cancellation_fee_percent: Number(form.late_cancellation_fee_percent) || 0,
  insurance_included: Boolean(form.insurance_included),
  verification_auto_checks: form.verification_auto_checks || [],
  min_driver_age: nullableInt(form.min_driver_age),
  min_driving_experience_years: nullableInt(form.min_driving_experience_years),
});

/**
 * Список проверок для автозапуска: каталог приходит с сервера, чтобы панель не
 * знала состав проверок заранее. Платные проверки IDX выделены, локальные
 * бесплатны и работают без ключей провайдера.
 */
function AutoChecksPicker({catalog, selected, onChange}) {
  if (!catalog) { return <State>Загрузка каталога проверок…</State>; }
  const idx = catalog.providers.find(provider => provider.name === 'idx');
  const idxConfigured = Boolean(idx && idx.configured);
  const groups = catalog.checks.reduce((result, check) => {
    const group = check.group || 'Другие проверки';
    if (!result[group]) { result[group] = []; }
    result[group].push(check);
    return result;
  }, {});
  const toggle = type => onChange(
    selected.includes(type) ? selected.filter(item => item !== type) : [...selected, type],
  );
  const paidSelected = selected.filter(type => {
    const check = catalog.checks.find(item => item.type === type);
    return check && check.provider !== 'local';
  }).length;

  return (
    <div>
      <Actions>
        <ActionButton type="button" onClick={() => onChange(catalog.recommended_types || [])}>
          Рекомендуемый набор
        </ActionButton>
        <ActionButton type="button" onClick={() => onChange(catalog.local_types || [])}>
          Только локальные
        </ActionButton>
        <ActionButton type="button" onClick={() => onChange([])}>
          Выключить автозапуск
        </ActionButton>
      </Actions>
      {!idxConfigured && (
        <State left>
          Провайдер IDX не подключён: отмеченные проверки IDX будут записаны как пропущенные,
          пока в окружении API не заданы ключи доступа. Локальные проверки работают всегда.
        </State>
      )}
      {Object.keys(groups).map(group => (
        <CheckGroup key={group}>
          <GroupTitle>{group}</GroupTitle>
          <CheckList>
            {groups[group].map(check => (
              <CheckItem key={check.type} recommended={check.recommended}>
                <label>
                  <input
                    type="checkbox"
                    checked={selected.includes(check.type)}
                    onChange={() => toggle(check.type)}
                  />
                  <span>
                    {check.title || checkTitle(check.type)}
                    <CheckMeta>
                      {check.provider === 'local' ? 'локальная, бесплатно' : 'IDX, платный запрос'}
                      {check.recommended ? ' · рекомендуется' : ''}
                    </CheckMeta>
                  </span>
                </label>
                {check.note && <CheckNote>{check.note}</CheckNote>}
              </CheckItem>
            ))}
          </CheckList>
        </CheckGroup>
      ))}
      <State left>
        Выбрано проверок: {selected.length}, из них платных запросов IDX: {paidSelected}.
        Автозапуск не повторяет успешную проверку с теми же данными моложе 30 дней —
        пересъёмка документов не оплачивается дважды.
      </State>
    </div>
  );
}

function RentalSettingsContent({authorization}) {
  const [form, setForm] = React.useState(null);
  const [catalog, setCatalog] = React.useState(null);
  const [state, setState] = React.useState('loading');
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  const load = React.useCallback(async () => {
    if (!authorization) { return; }
    setState('loading');
    try {
      const [settings, verificationCatalog] = await Promise.all([
        getRentalSettings(authorization),
        getVerificationCatalog(authorization).catch(() => null),
      ]);
      setForm(toForm(settings));
      setCatalog(verificationCatalog);
      setState('ready');
    } catch (requestError) {
      setError(requestError.message);
      setState('ready');
    }
  }, [authorization]);

  React.useEffect(() => { load(); }, [load]);

  const change = (field, value) => setForm(current => ({...current, [field]: value}));

  const submit = async () => {
    setError('');
    setSuccess('');
    setState('saving');
    try {
      const saved = await updateRentalSettings(authorization, toPayload(form));
      setForm(toForm(saved));
      setSuccess('Настройки сохранены');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setState('ready');
    }
  };

  return (
    <>
      {state === 'loading' || !form ? (
        <State>Загрузка…</State>
      ) : (
        <Column>
          <Card>
            <CardTitle>Срок и удержание</CardTitle>
            <FieldGrid>
              <Field>
                <span>Минимальный срок аренды, суток</span>
                <Input
                  type="number"
                  min="1"
                  value={form.min_rental_days}
                  onChange={event => change('min_rental_days', event.target.value)}
                />
              </Field>
              <Field>
                <span>Максимальный срок аренды, суток</span>
                <Input
                  type="number"
                  min="1"
                  value={form.max_rental_days}
                  onChange={event => change('max_rental_days', event.target.value)}
                />
              </Field>
              <Field>
                <span>Удержание под оплату, минут</span>
                <Input
                  type="number"
                  min="5"
                  value={form.payment_hold_minutes}
                  onChange={event => change('payment_hold_minutes', event.target.value)}
                />
              </Field>
              <Field>
                <span>Повторная попытка оплаты, минут</span>
                <Input
                  type="number"
                  min="0"
                  value={form.payment_retry_minutes}
                  onChange={event => change('payment_retry_minutes', event.target.value)}
                />
              </Field>
            </FieldGrid>
          </Card>

          <Card>
            <CardTitle>Депозит и ответственность</CardTitle>
            <FieldGrid>
              <Field>
                <span>Депозит, ₽</span>
                <Input
                  type="number"
                  min="0"
                  step="100"
                  value={form.deposit_amount_rub}
                  onChange={event => change('deposit_amount_rub', event.target.value)}
                />
              </Field>
              <Field>
                <span>Предел ответственности клиента, ₽</span>
                <Input
                  type="number"
                  min="0"
                  step="100"
                  value={form.liability_amount_rub}
                  onChange={event => change('liability_amount_rub', event.target.value)}
                />
              </Field>
              <Field>
                <span>Лимит пробега, км в сутки</span>
                <Input
                  type="number"
                  min="1"
                  value={form.mileage_limit_per_day}
                  onChange={event => change('mileage_limit_per_day', event.target.value)}
                />
              </Field>
              <Field>
                <span>Цена превышения, ₽ за км</span>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={form.mileage_overrun_price_rub}
                  onChange={event => change('mileage_overrun_price_rub', event.target.value)}
                />
              </Field>
              <Field>
                <span>Просрочка возврата, ₽ за час</span>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={form.overdue_hour_price_rub}
                  onChange={event => change('overdue_hour_price_rub', event.target.value)}
                />
              </Field>
            </FieldGrid>
            <Field style={{marginTop: 16}}>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={form.insurance_included}
                  onChange={event => change('insurance_included', event.target.checked)}
                />
                Страховка включена в стоимость аренды
              </CheckboxRow>
            </Field>
            <State left>
              Депозит списывается вместе с предоплатой отдельной операцией. Просрочка и перерасход
              пробега гасятся из депозита первыми; на что депозита не хватило, становится
              задолженностью и добирается с карты. Лимит пробега считается на весь срок аренды,
              а не на каждые сутки отдельно.
            </State>
          </Card>

          <Card>
            <CardTitle>Отмена и напоминания</CardTitle>
            <FieldGrid>
              <Field>
                <span>Бесплатная отмена, часов до выдачи</span>
                <Input
                  type="number"
                  min="0"
                  value={form.free_cancellation_hours}
                  onChange={event => change('free_cancellation_hours', event.target.value)}
                />
              </Field>
              <Field>
                <span>Удержание при поздней отмене, % предоплаты</span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={form.late_cancellation_fee_percent}
                  onChange={event => change('late_cancellation_fee_percent', event.target.value)}
                />
              </Field>
              <Field>
                <span>Напоминания о возврате, минут до срока</span>
                <Input
                  value={form.return_reminder_offsets}
                  onChange={event => change('return_reminder_offsets', event.target.value)}
                />
              </Field>
            </FieldGrid>
            <State left>
              Напоминания задаются через запятую. По умолчанию 300, 180, 60: два push и финальная
              SMS за час. Удержание применяется, только когда процент больше нуля: при 0 отмена
              бесплатна в любой момент, независимо от порога.
            </State>
          </Card>

          <Card>
            <CardTitle>Выдача автомобиля</CardTitle>
            <Field>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={form.handover_by_operator}
                  onChange={event => change('handover_by_operator', event.target.checked)}
                />
                Аренду открывает оператор при выдаче
              </CheckboxRow>
            </Field>
            <FieldGrid style={{marginTop: 16}}>
              <Field>
                <span>Время получения по умолчанию</span>
                <Input
                  type="time"
                  value={form.default_pickup_time}
                  onChange={event => change('default_pickup_time', event.target.value)}
                />
              </Field>
              <Field>
                <span>Обслуживание между арендами, минут</span>
                <Input
                  type="number"
                  min="0"
                  max="1440"
                  value={form.service_buffer_minutes}
                  onChange={event => change('service_buffer_minutes', event.target.value)}
                />
              </Field>
              <Field>
                <span>Освобождение по неявке, минут после времени выдачи</span>
                <Input
                  type="number"
                  min="15"
                  max="10080"
                  value={form.no_show_release_minutes}
                  onChange={event => change('no_show_release_minutes', event.target.value)}
                />
              </Field>
            </FieldGrid>
            <State left>
              Время получения клиент выбирает сам в часы работы пункта; здесь — значение,
              с которого открывается пикер. Буфер обслуживания добавляется после каждой
              аренды: раньше, чем через это время, машина не попадёт в следующую выдачу —
              приложение покажет «доступно с …». Пока флаг выдачи включён, аренду открывает
              оператор после осмотра; срок неявки отсчитывается от времени получения.
            </State>
          </Card>

          <Card>
            <CardTitle>Проверки клиента после подачи документов</CardTitle>
            <FieldGrid>
              <Field>
                <span>Минимальный возраст водителя, лет</span>
                <Input
                  type="number"
                  min="18"
                  max="99"
                  placeholder="не задан"
                  value={form.min_driver_age}
                  onChange={event => change('min_driver_age', event.target.value)}
                />
              </Field>
              <Field>
                <span>Минимальный стаж, лет</span>
                <Input
                  type="number"
                  min="0"
                  max="80"
                  placeholder="не задан"
                  value={form.min_driving_experience_years}
                  onChange={event => change('min_driving_experience_years', event.target.value)}
                />
              </Field>
            </FieldGrid>
            <State left>
              Пустое значение отключает правило: проверка «Возраст и стаж водителя» станет
              информационной. Стаж берётся из ответа ГИБДД, иначе по дате выдачи ВУ.
            </State>
            <SectionLabel>Запускать автоматически</SectionLabel>
            <AutoChecksPicker
              catalog={catalog}
              selected={form.verification_auto_checks}
              onChange={value => change('verification_auto_checks', value)}
            />
            <State left>
              Отмеченные проверки ставятся в очередь сразу после подачи комплекта документов
              и выполняются фоновым процессом; результаты и оценка риска появляются в карточке
              KYC. Проверки только подсказывают — решение по клиенту принимает оператор.
              Скоринг БКИ, срок жизни и активность номера требуют согласия клиента: включайте
              их после подтверждения юристом, что текст согласия в приложении его покрывает.
            </State>
          </Card>

          <Card>
            <CardTitle>Сохранение</CardTitle>
            {error && <ErrorText>{error}</ErrorText>}
            {success && <SuccessText>{success}</SuccessText>}
            <Actions>
              <ActionButton primary disabled={state === 'saving'} onClick={submit}>
                {state === 'saving' ? 'Сохранение…' : 'Сохранить настройки'}
              </ActionButton>
              <ActionButton onClick={load}>Отменить изменения</ActionButton>
            </Actions>
          </Card>
        </Column>
      )}
    </>
  );
}

/**
 * Настройки идут одной колонкой: секции разной высоты в двух колонках
 * разъезжались, и соседние блоки переставали читаться как один список правил.
 */
const Column = styled(Grid)`
  grid-template-columns: minmax(0, 1fr);
  max-width: 880px;
`;

const SectionLabel = styled.h3`
  margin: 20px 0 10px;
  color: ${colors.TEXT_TITLE};
  font-size: 14px;
`;
const CheckGroup = styled.section`margin: 14px 0;`;
const GroupTitle = styled.h4`
  margin: 0 0 8px;
  color: ${colors.TEXT_MINOR};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;
const CheckList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
`;
const CheckItem = styled.div`
  padding: 10px 12px;
  border: 1px solid ${props => (props.recommended ? colors.BLUE_MAIN : colors.LINE)};
  border-radius: 6px;
  font-size: 13px;
  label { display: flex; align-items: flex-start; gap: 9px; margin: 0; cursor: pointer; }
  input { width: 18px; height: 18px; flex: 0 0 auto; margin-top: 1px; }
`;
const CheckMeta = styled.span`
  display: block;
  margin-top: 3px;
  color: ${colors.TEXT_MINOR};
  font-size: 11px;
`;
const CheckNote = styled.div`
  margin: 7px 0 0 27px;
  color: ${colors.TEXT_MINOR};
  font-size: 12px;
  line-height: 1.4;
`;

/**
 * Обёртка с Layout: содержимое вынесено отдельно, чтобы получить authorization.
 */
function RentalSettings() {
  return (
    <Layout
      title="Настройки проката"
      description="Срок аренды, депозит и правила расчёта. Значения применяются ко всем новым заявкам сразу после сохранения."
    >
      <RentalSettingsContent />
    </Layout>
  );
}

export default RentalSettings;
